import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { formatDateTime } from '../utils/dateFormat';

const ROLE_OPTIONS = ['device_owner', 'monitor', 'admin'];
const ALL_ROLES = ['super_admin', 'admin', 'device_owner', 'monitor'];

function RoleBadge({ role }) {
  const map = {
    super_admin: 'bg-purple-50 text-purple-700',
    admin: 'bg-blue-50 text-blue-700',
    device_owner: 'bg-emerald-50 text-emerald-700',
    monitor: 'bg-amber-50 text-amber-700',
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${map[role] || 'bg-secondary-100 text-secondary-600'}`}>
      {role?.replace(/_/g, ' ')}
    </span>
  );
}

function ActiveBadge({ active }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${active ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}>
      {active ? 'Yes' : 'No'}
    </span>
  );
}

const emptyForm = { email: '', password: '', firstName: '', lastName: '', role: 'device_owner', phone: '' };

export default function Users() {
  const { user: currentUser, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('created_at');
  const [order, setOrder] = useState('desc');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [detailLoadingId, setDetailLoadingId] = useState(null);
  const [userDetails, setUserDetails] = useState({});

  // Monitor device-scoping (RBAC): which devices a monitor user is allowed to see.
  const [userPermissions, setUserPermissions] = useState({}); // userId -> [perm]
  const [permLoadingId, setPermLoadingId] = useState(null);
  const [deviceOptions, setDeviceOptions] = useState([]);
  const [permDeviceSel, setPermDeviceSel] = useState({}); // userId -> deviceId

  const isAdmin = currentUser && ['admin', 'super_admin'].includes(currentUser.role);

  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ ...emptyForm });
  const [addError, setAddError] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [editError, setEditError] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20, sortBy, order };
      if (roleFilter) params.role = roleFilter;
      if (search.trim()) params.q = search.trim();
      const { data } = await api.get('/users', { params });
      setUsers(data.data);
      setPagination(data.pagination);
    } catch {} finally {
      setLoading(false);
    }
  }, [page, roleFilter, search, sortBy, order]);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleSort = (col) => {
    if (sortBy === col) {
      setOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(col);
      setOrder('asc');
    }
    setPage(1);
  };

  const sortIcon = (col) => {
    if (sortBy !== col) return '';
    return order === 'asc' ? ' \u2191' : ' \u2193';
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    setAddLoading(true);
    try {
      await api.post('/users', {
        email: addForm.email,
        password: addForm.password,
        firstName: addForm.firstName,
        lastName: addForm.lastName,
        role: addForm.role,
        phone: addForm.phone || undefined,
      });
      setShowAdd(false);
      setAddForm({ ...emptyForm });
      fetchUsers();
    } catch (err) {
      setAddError(err.response?.data?.error || 'Failed to create user');
    } finally {
      setAddLoading(false);
    }
  };

  const startEdit = (user) => {
    setEditId(user.id);
    setEditForm({
      email: user.email,
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      role: user.role,
      phone: user.phone || '',
    });
    setEditError('');
  };

  const cancelEdit = () => {
    setEditId(null);
    setEditForm({});
    setEditError('');
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setEditError('');
    setEditLoading(true);
    try {
      await api.put(`/users/${editId}`, {
        email: editForm.email,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        role: editForm.role,
        phone: editForm.phone || undefined,
      });
      cancelEdit();
      fetchUsers();
    } catch (err) {
      setEditError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeactivate = async (user) => {
    if (!window.confirm(`Deactivate user "${user.email}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      fetchUsers();
    } catch {}
  };

  // Admins and super_admins may impersonate; admins cannot impersonate a
  // super_admin, and nobody impersonates themselves or an inactive account.
  const canImpersonate = (u) => {
    if (!currentUser || !['admin', 'super_admin'].includes(currentUser.role)) return false;
    if (!u.active || u.id === currentUser.id) return false;
    if (u.role === 'super_admin' && currentUser.role !== 'super_admin') return false;
    return true;
  };

  const handleImpersonate = async (u) => {
    const name = `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email;
    if (!window.confirm(`Impersonate ${name}? You will see the app exactly as they do until you exit.`)) return;
    try {
      await startImpersonation(u.id);
      navigate('/');
    } catch (err) {
      window.alert(err.response?.data?.error || 'Failed to start impersonation');
    }
  };

  const fetchPermissions = async (userId) => {
    setPermLoadingId(userId);
    try {
      const { data } = await api.get(`/users/${userId}/permissions`);
      setUserPermissions((prev) => ({ ...prev, [userId]: data.permissions || [] }));
    } catch {
      setUserPermissions((prev) => ({ ...prev, [userId]: [] }));
    } finally {
      setPermLoadingId(null);
    }
  };

  const loadDeviceOptions = async () => {
    if (deviceOptions.length) return;
    try {
      const { data } = await api.get('/devices', { params: { limit: 100 } });
      setDeviceOptions(data.data || []);
    } catch {}
  };

  const grantDevice = async (userId) => {
    const deviceId = permDeviceSel[userId];
    if (!deviceId) return;
    try {
      await api.post(`/users/${userId}/permissions`, { deviceId, permissionType: 'monitor' });
      setPermDeviceSel((prev) => ({ ...prev, [userId]: '' }));
      fetchPermissions(userId);
    } catch (err) {
      window.alert(err.response?.data?.error || 'Failed to grant device');
    }
  };

  const revokeDevice = async (userId, deviceId) => {
    try {
      await api.delete(`/users/${userId}/permissions/${deviceId}`);
      fetchPermissions(userId);
    } catch {}
  };

  const toggleDetails = async (user) => {
    const userId = user.id;
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      return;
    }

    setExpandedUserId(userId);

    // For monitor users, load their device-scoping grants + a device picker.
    if (user.role === 'monitor' && isAdmin) {
      fetchPermissions(userId);
      loadDeviceOptions();
    }

    if (userDetails[userId]) return;

    setDetailLoadingId(userId);
    try {
      const { data } = await api.get(`/users/${userId}`);
      setUserDetails((prev) => ({ ...prev, [userId]: data }));
    } catch {
      setUserDetails((prev) => ({ ...prev, [userId]: { devices: [] } }));
    } finally {
      setDetailLoadingId(null);
    }
  };

  const inputClass = 'px-3 py-1.5 border border-secondary-200 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none';
  const colCount = 8;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Users</h1>
          <p className="text-secondary-500 text-sm mt-1">{pagination.total || 0} users total</p>
        </div>
        <button
          onClick={() => { setShowAdd(!showAdd); setAddError(''); }}
          className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition"
        >
          {showAdd ? 'Cancel' : 'Add User'}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search by email, name, or phone"
          className="px-4 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none sm:min-w-[280px]"
        />
        <select
          value={roleFilter}
          onChange={(e) => { setRoleFilter(e.target.value); setPage(1); }}
          className="px-4 py-2 border border-secondary-200 rounded-lg text-sm text-secondary-700 focus:ring-2 focus:ring-primary-500 outline-none"
        >
          <option value="">All roles</option>
          {ALL_ROLES.map((r) => (
            <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
          ))}
        </select>
      </div>

      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-secondary-50 text-secondary-600">
              <tr>
                <th className="px-5 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('email')}>
                  Email{sortIcon('email')}
                </th>
                <th className="px-5 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('first_name')}>
                  Name{sortIcon('first_name')}
                </th>
                <th className="px-5 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('role')}>
                  Role{sortIcon('role')}
                </th>
                <th className="px-5 py-3 text-left font-medium">Phone</th>
                <th className="px-5 py-3 text-left font-medium">Active</th>
                <th className="px-5 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('last_login_at')}>
                  Last Login{sortIcon('last_login_at')}
                </th>
                <th className="px-5 py-3 text-left font-medium cursor-pointer select-none" onClick={() => handleSort('created_at')}>
                  Created{sortIcon('created_at')}
                </th>
                <th className="px-5 py-3 text-left font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {showAdd && (
                <tr className="bg-primary-50/30">
                  <td colSpan={colCount} className="px-5 py-4">
                    <form onSubmit={handleAdd} className="flex flex-wrap items-end gap-3">
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-secondary-500 font-medium">Email *</label>
                        <input type="email" required value={addForm.email}
                          onChange={(e) => setAddForm({ ...addForm, email: e.target.value })}
                          className={inputClass} placeholder="user@example.com" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-secondary-500 font-medium">Password *</label>
                        <input type="password" required value={addForm.password}
                          onChange={(e) => setAddForm({ ...addForm, password: e.target.value })}
                          className={inputClass} placeholder="Min 8 chars" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-secondary-500 font-medium">First Name *</label>
                        <input type="text" required value={addForm.firstName}
                          onChange={(e) => setAddForm({ ...addForm, firstName: e.target.value })}
                          className={inputClass} placeholder="First" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-secondary-500 font-medium">Last Name *</label>
                        <input type="text" required value={addForm.lastName}
                          onChange={(e) => setAddForm({ ...addForm, lastName: e.target.value })}
                          className={inputClass} placeholder="Last" />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-secondary-500 font-medium">Role *</label>
                        <select value={addForm.role}
                          onChange={(e) => setAddForm({ ...addForm, role: e.target.value })}
                          className={inputClass}>
                          {ROLE_OPTIONS.map((r) => (
                            <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                          ))}
                        </select>
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="text-xs text-secondary-500 font-medium">Phone</label>
                        <input type="tel" value={addForm.phone}
                          onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })}
                          className={inputClass} placeholder="Optional" />
                      </div>
                      <button type="submit" disabled={addLoading}
                        className="px-4 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50">
                        {addLoading ? 'Creating...' : 'Create'}
                      </button>
                      {addError && <p className="text-red-600 text-xs w-full">{addError}</p>}
                    </form>
                  </td>
                </tr>
              )}

              {loading && (
                <tr><td colSpan={colCount} className="px-5 py-8 text-center text-secondary-400">Loading...</td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={colCount} className="px-5 py-8 text-center text-secondary-400">No users found</td></tr>
              )}
              {!loading && users.map((u) => (
                <React.Fragment key={u.id}>
                  {editId === u.id ? (
                    <tr className="bg-amber-50/30">
                      <td colSpan={colCount} className="px-5 py-4">
                        <form onSubmit={handleEdit} className="flex flex-wrap items-end gap-3">
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary-500 font-medium">Email</label>
                            <input type="email" required value={editForm.email}
                              onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                              className={inputClass} />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary-500 font-medium">First Name</label>
                            <input type="text" required value={editForm.firstName}
                              onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
                              className={inputClass} />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary-500 font-medium">Last Name</label>
                            <input type="text" required value={editForm.lastName}
                              onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
                              className={inputClass} />
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary-500 font-medium">Role</label>
                            <select value={editForm.role}
                              onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                              className={inputClass}>
                              {(editForm.role === 'super_admin' ? ALL_ROLES : ROLE_OPTIONS).map((r) => (
                                <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>
                              ))}
                            </select>
                          </div>
                          <div className="flex flex-col gap-1">
                            <label className="text-xs text-secondary-500 font-medium">Phone</label>
                            <input type="tel" value={editForm.phone}
                              onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                              className={inputClass} />
                          </div>
                          <button type="submit" disabled={editLoading}
                            className="px-4 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50">
                            {editLoading ? 'Saving...' : 'Save'}
                          </button>
                          <button type="button" onClick={cancelEdit}
                            className="px-4 py-1.5 border border-secondary-200 text-secondary-600 text-sm font-medium rounded-lg hover:bg-secondary-50 transition">
                            Cancel
                          </button>
                          {editError && <p className="text-red-600 text-xs w-full">{editError}</p>}
                        </form>
                      </td>
                    </tr>
                  ) : (
                    <tr className="hover:bg-secondary-50 transition">
                      <td className="px-5 py-3 text-secondary-700">{u.email}</td>
                      <td className="px-5 py-3 text-secondary-700">
                        {u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '--'}
                      </td>
                      <td className="px-5 py-3"><RoleBadge role={u.role} /></td>
                      <td className="px-5 py-3 text-secondary-500 text-xs">{u.phone || '--'}</td>
                      <td className="px-5 py-3"><ActiveBadge active={u.active} /></td>
                      <td className="px-5 py-3 text-secondary-500 text-xs whitespace-nowrap">{formatDateTime(u.last_login_at)}</td>
                      <td className="px-5 py-3 text-secondary-500 text-xs whitespace-nowrap">{formatDateTime(u.created_at)}</td>
                      <td className="px-5 py-3">
                        <div className="flex gap-1.5">
                          <button onClick={() => toggleDetails(u)}
                            className="px-2.5 py-1 text-xs bg-secondary-100 text-secondary-700 rounded-lg hover:bg-secondary-200 transition font-medium">
                            {expandedUserId === u.id ? 'Hide' : 'View'}
                          </button>
                          <button onClick={() => startEdit(u)}
                            className="px-2.5 py-1 text-xs bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition font-medium">
                            Edit
                          </button>
                          {canImpersonate(u) && (
                            <button onClick={() => handleImpersonate(u)}
                              className="px-2.5 py-1 text-xs bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100 transition font-medium">
                              Impersonate
                            </button>
                          )}
                          {u.active && (
                            <button onClick={() => handleDeactivate(u)}
                              className="px-2.5 py-1 text-xs bg-red-50 text-red-700 rounded-lg hover:bg-red-100 transition font-medium">
                              Deactivate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}

                  {expandedUserId === u.id && (
                    <tr className="bg-secondary-50/60">
                      <td colSpan={colCount} className="px-5 py-4">
                        {detailLoadingId === u.id ? (
                          <p className="text-sm text-secondary-400">Loading account overview...</p>
                        ) : (
                          <div className="space-y-3">
                            <div className="flex flex-wrap gap-6 text-sm">
                              <div>
                                <p className="text-xs text-secondary-400">User</p>
                                <p className="font-medium text-secondary-800">
                                  {u.first_name || u.last_name ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : u.email}
                                </p>
                              </div>
                              <div>
                                <p className="text-xs text-secondary-400">Devices</p>
                                <p className="font-medium text-secondary-800">{userDetails[u.id]?.devices?.length || 0}</p>
                              </div>
                            </div>

                            <div className="overflow-x-auto">
                              <table className="w-full text-sm">
                                <thead className="bg-white text-secondary-600">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-medium">Device</th>
                                    <th className="px-3 py-2 text-left font-medium">Subscription</th>
                                    <th className="px-3 py-2 text-left font-medium">Firmware</th>
                                    <th className="px-3 py-2 text-left font-medium">Status</th>
                                    <th className="px-3 py-2 text-left font-medium">Last Seen</th>
                                    <th className="px-3 py-2 text-left font-medium">Open</th>
                                  </tr>
                                </thead>
                                <tbody className="divide-y divide-secondary-100 bg-white">
                                  {(userDetails[u.id]?.devices || []).length === 0 && (
                                    <tr>
                                      <td colSpan={6} className="px-3 py-4 text-secondary-400 text-center">
                                        No devices registered to this user
                                      </td>
                                    </tr>
                                  )}
                                  {(userDetails[u.id]?.devices || []).map((device) => (
                                    <tr key={device.deviceId}>
                                      <td className="px-3 py-2">
                                        <div className="font-medium text-secondary-800">{device.name}</div>
                                        <div className="text-xs text-secondary-500 font-mono">{device.deviceId}</div>
                                      </td>
                                      <td className="px-3 py-2 text-xs text-secondary-600">
                                        {device.subscription
                                          ? `${device.subscription.plan} (${device.subscription.status})`
                                          : 'No active subscription'}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-secondary-600">
                                        {device.firmwareVersion || '--'} / {(device.boardModel || 'v3').toUpperCase()}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-secondary-600">
                                        {device.status} · {(device.provisioningStatus || 'registered').replace(/_/g, ' ')}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-secondary-600">{formatDateTime(device.lastSeenAt)}</td>
                                      <td className="px-3 py-2">
                                        <Link
                                          to={`/devices/${device.deviceId}`}
                                          className="px-2.5 py-1 text-xs bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition font-medium inline-block"
                                        >
                                          Open Device
                                        </Link>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            {u.role === 'monitor' && isAdmin && (
                              <div className="border-t border-secondary-200 pt-3 mt-1">
                                <p className="text-xs font-semibold text-secondary-600 mb-2">
                                  Monitored devices (access scope)
                                </p>
                                {permLoadingId === u.id ? (
                                  <p className="text-sm text-secondary-400">Loading permissions...</p>
                                ) : (
                                  <>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                      {(userPermissions[u.id] || []).length === 0 && (
                                        <span className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded-lg">
                                          No devices assigned — this monitor currently sees nothing.
                                        </span>
                                      )}
                                      {(userPermissions[u.id] || []).map((p) => (
                                        <span key={p.device_id}
                                          className="inline-flex items-center gap-2 px-2.5 py-1 bg-white border border-secondary-200 rounded-lg text-xs">
                                          <span className="font-mono text-secondary-700">{p.device_id}</span>
                                          {p.device_name && <span className="text-secondary-500">{p.device_name}</span>}
                                          <button onClick={() => revokeDevice(u.id, p.device_id)}
                                            className="text-red-600 hover:text-red-700 font-bold leading-none" title="Remove">
                                            &times;
                                          </button>
                                        </span>
                                      ))}
                                    </div>
                                    <div className="flex flex-wrap items-center gap-2">
                                      <select
                                        value={permDeviceSel[u.id] || ''}
                                        onChange={(e) => setPermDeviceSel((prev) => ({ ...prev, [u.id]: e.target.value }))}
                                        className={inputClass}>
                                        <option value="">Select a device...</option>
                                        {deviceOptions.map((d) => (
                                          <option key={d.device_id} value={d.device_id}>
                                            {d.device_id}{(d.location || d.name) ? ` - ${d.location || d.name}` : ''}
                                          </option>
                                        ))}
                                      </select>
                                      <button onClick={() => grantDevice(u.id)} disabled={!permDeviceSel[u.id]}
                                        className="px-3 py-1.5 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition disabled:opacity-50">
                                        Add device
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="px-5 py-3 border-t border-secondary-100 flex items-center justify-between">
            <p className="text-sm text-secondary-500">Page {pagination.page} of {pagination.totalPages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Prev</button>
              <button onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))} disabled={page >= pagination.totalPages}
                className="px-3 py-1 text-sm rounded-lg border border-secondary-200 hover:bg-secondary-50 disabled:opacity-40">Next</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
